import { useEffect } from "react";
import { useFormContext, useFieldArray } from "react-hook-form";
import { Row, Col, InputGroup } from "react-bootstrap";
import { useIntl } from "react-intl";

import Dropdown from "components/atoms/Dropdown/Dropdown";
import FormField from "components/molecules/FormField/FormField";
import Radio from "components/atoms/Radio/Radio";
import Input from "components/atoms/Input/Input";
import { CustomServiceConfirmationQuestion } from "helpers/quotationFormHelper";
import FormHr from "pages/shipper/RequestForm/components/Common/FormHr";

type CustomServiceConfirmationField = {
  custom_id: string;
  id: number;
  value: string | string[];
  comment: string;
};

type FormValues = {
  service: {
    freight_mode: string;
  };
  service_confirmation: {
    custom: Array<CustomServiceConfirmationField>;
  };
};

function CustomServiceConfirmation({
  quotationConfigData,
}: {
  quotationConfigData: { custom_service_confirmation: Record<string, CustomServiceConfirmationQuestion[]> };
}) {
  const intl = useIntl();
  const { getValues, watch, control, setValue } = useFormContext<FormValues>();
  const selectedFreightMode: string = watch("service.freight_mode");
  const customServiceQuestions = quotationConfigData?.custom_service_confirmation[selectedFreightMode];

  const { append, fields, remove } = useFieldArray<FormValues, "service_confirmation.custom", "custom_id">({
    control,
    name: "service_confirmation.custom",
    keyName: "custom_id",
  });

  useEffect(() => {
    if (!customServiceQuestions || fields.length > 0) return;
    customServiceQuestions.forEach((question: CustomServiceConfirmationQuestion, index: number) => {
      if (!fields[index]) {
        append({ value: "", id: question.question_pk });
      }
    });
  }, [customServiceQuestions]);

  // Remove service_confirmation.custom fields when freight mode changes
  // that are not linked to the new freight mode
  // and add new fields for the new freight mode
  useEffect(() => {
    if (!customServiceQuestions?.length) return;
    const newFormValues = (watch("service_confirmation.custom") ?? []).filter((formValue) =>
      customServiceQuestions?.some((question) => formValue.id && question.question_pk === formValue.id),
    );
    setValue("service_confirmation.custom", newFormValues);

    if (!fields?.length) return;
    fields.forEach((field: any, index) => {
      if (!customServiceQuestions.some((question: any) => question.question_pk === field.id)) remove(index);
    });

    customServiceQuestions.forEach((question: CustomServiceConfirmationQuestion) => {
      if (!fields.some((field: any) => field.id === question.question_pk)) {
        append({ value: "", id: question.question_pk });
      }
    });
  }, [customServiceQuestions]);

  const serviceOptions = [
    {
      value: "Yes",
      label: intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" }),
    },
    {
      value: "No",
      label: intl.formatMessage({ id: "general.no", defaultMessage: "No" }),
    },
  ];

  const getServiceConfirmationValueByIndex = (index: number) => watch(`service_confirmation.custom.${index}.value`);
  const getQuestionById = (id: number) => customServiceQuestions?.find((question) => question.question_pk === id);

  const renderQuestion = (field: CustomServiceConfirmationField, fieldIndex: number) => {
    const question = getQuestionById(field.id);
    switch (question?.question_type) {
      case "multiple_choices":
        if (!question.allow_multiple_answers) {
          return (
            <Dropdown
              name={`service_confirmation.custom.${fieldIndex}.value`}
              options={
                question.multiple_option_choices?.map((option) => ({
                  value: option,
                  label: option,
                })) || []
              }
              useHookForm
              required
              placeholder={intl.formatMessage({
                id: "tracking.pleaseSelect",
                defaultMessage: "Please select",
              })}
            />
          );
        }
        return (
          <div>
            {question.multiple_option_choices?.map((option, optionIndex) => (
              <div key={optionIndex} className="mb-2">
                <InputGroup className="checkbox bg-transparent">
                  <label className="d-flex align-items-center cursor-pointer mb-0 w-100">
                    <input
                      type="checkbox"
                      className="d-none"
                      checked={(getServiceConfirmationValueByIndex(fieldIndex) || []).includes(option)}
                      onChange={(e) => {
                        const val = getValues(`service_confirmation.custom.${fieldIndex}.value`);
                        const currentValue = Array.isArray(val) ? val : [];
                        setValue(
                          `service_confirmation.custom.${fieldIndex}.value`,
                          e.target.checked ? [...currentValue, option] : currentValue.filter((value) => value !== option),
                        );
                      }}
                    />
                    <span className="border-dark border-solid border-1 mr-11 position-relative br-3 checkbox__box" />
                    <span style={{ color: "black" }}>{option}</span>
                  </label>
                </InputGroup>
              </div>
            ))}
          </div>
        );
      case "yes/no":
        return (
          <>
            <Radio
              options={serviceOptions}
              name={`service_confirmation.custom.${fieldIndex}.value`}
              defaultChecked={getValues(`service_confirmation.custom.${fieldIndex}.value`) || "Yes"}
              customOption
              className="border-0 w-50 task-dialog-radio"
              eventContext="Task Dialog"
              required
            />
            {watch(`service_confirmation.custom.${fieldIndex}.value`) === "No" && (
              <div>
                <Input
                  type="text"
                  name={`service_confirmation.custom.${fieldIndex}.comment`}
                  placeholder={intl.formatMessage({
                    id: "billing.newCommentPlaceholder",
                    defaultMessage: "Your optional comment...",
                  })}
                  eventContext="Task Dialog"
                  className="rounded-pill input-width-lg h-26"
                />
              </div>
            )}
          </>
        );
      case "free_text":
        return <FormField type="text" mandatory field_id={`service_confirmation.custom.${fieldIndex}.value`} />;
      default:
        return null;
    }
  };

  return (
    <div>
      <Row className="mb-24">
        <Col lg={2} md={3} />
        <Col lg={6} md={9}>
          {fields.map((field, index) => (
            <Row key={field.custom_id} className="mb-3">
              <Col lg={4}>
                <label className="required font-weight-bold">{getQuestionById(field.id)?.question}</label>
              </Col>
              <Col lg={8}>{renderQuestion(field, index)}</Col>
            </Row>
          ))}
        </Col>
      </Row>
      <FormHr />
    </div>
  );
}

export default CustomServiceConfirmation;
